import { createArizonaMockCoordinate, createSeededMockRandom, isWithinArizonaMockBounds } from "./mock-geo.js";
import { assertDateOnly } from "./time-series.js";

export const MOCK_ANIMAL_REPORT_COUNT = 180;
export const MOCK_ENVIRONMENT_REPORT_COUNT = 180;

const OVERLAY_START_DATE = "2026-01-01";
const OVERLAY_DAY_COUNT = 181;
const ANIMAL_SEED = 325_4101;
const ENVIRONMENT_SEED = 325_4102;

const ANIMAL_SUBTYPES = Object.freeze(["wildlife_carcass", "livestock_illness", "unusual_animal_behavior"]);
const ENVIRONMENT_SUBTYPES = Object.freeze(["air_quality", "insect_vectors", "water_contamination", "flooding"]);
const SEVERITIES = Object.freeze(["low", "medium", "high"]);

export function createMockOverlayReports({
  animalCount = MOCK_ANIMAL_REPORT_COUNT,
  environmentCount = MOCK_ENVIRONMENT_REPORT_COUNT,
  animalSeed = ANIMAL_SEED,
  environmentSeed = ENVIRONMENT_SEED,
} = {}) {
  return Object.freeze({
    animals: createMockOverlayCategory("animals", animalCount, animalSeed, ANIMAL_SUBTYPES),
    environment: createMockOverlayCategory("environment", environmentCount, environmentSeed, ENVIRONMENT_SUBTYPES),
  });
}

export function validateMockOverlayReport(report, index = 0) {
  if (!report || typeof report !== "object") throw new TypeError(`Invalid overlay report at index ${index}: expected an object.`);
  if (!Object.values(OVERLAY_CATEGORIES).includes(report.category)) {
    throw new TypeError(`Invalid overlay report at index ${index}: category must be animals or environment.`);
  }
  if (!Number.isFinite(report.latitude) || !Number.isFinite(report.longitude)) {
    throw new TypeError(`Invalid overlay report at index ${index}: latitude and longitude must be finite numbers.`);
  }
  if (report.latitude < -90 || report.latitude > 90) throw new RangeError(`Invalid overlay report at index ${index}: latitude must be between -90 and 90.`);
  if (report.longitude < -180 || report.longitude > 180) throw new RangeError(`Invalid overlay report at index ${index}: longitude must be between -180 and 180.`);
  assertDateOnly(report.reportDate, `reportDate for overlay report at index ${index}`);
  if (typeof report.subtype !== "string" || report.subtype.trim() === "") throw new TypeError(`Invalid overlay report at index ${index}: subtype is required.`);
  if (typeof report.severity !== "string" || report.severity.trim() === "") throw new TypeError(`Invalid overlay report at index ${index}: severity is required.`);
  return report;
}

export { isWithinArizonaMockBounds };

const OVERLAY_CATEGORIES = Object.freeze(["animals", "environment"]);

function createMockOverlayCategory(category, count, seed, subtypes) {
  if (!Number.isInteger(count) || count < 0) throw new RangeError(`Expected a non-negative integer ${category} mock-report count.`);
  const random = createSeededMockRandom(seed);
  return Object.freeze(Array.from({ length: count }, (_, index) => {
    const report = Object.freeze({
      id: `mock-${category === "animals" ? "animal" : "environment"}-${String(index + 1).padStart(4, "0")}`,
      category,
      ...createArizonaMockCoordinate(random),
      reportDate: dateForOverlayIndex(index, random),
      subtype: subtypes[Math.floor(random() * subtypes.length)],
      severity: SEVERITIES[Math.floor(random() * SEVERITIES.length)],
    });
    return validateMockOverlayReport(report, index);
  }));
}

function dateForOverlayIndex(index, random) {
  if (index === 0) return OVERLAY_START_DATE;
  if (index === 1) return "2026-06-30";
  const dayOffset = Math.floor(random() * OVERLAY_DAY_COUNT);
  return new Date(Date.UTC(2026, 0, 1 + dayOffset)).toISOString().slice(0, 10);
}
